import {
	createBillingSqlExecutor,
	resolveBillingConnectionIdByWebhookIngressToken,
} from "../billing/import/postgres.ts";
import { billingWebhookIngressObservabilityFromInternals } from "../billing/ingestion/observability/from-internals.ts";
import { recordBillingWebhookIngressQuietly } from "../billing/ingestion/observability/health.ts";
import {
	billingWebhookRegistrationKind,
	persistableWebhookRejectionStage,
	publicBillingRejectionCode,
} from "../billing/ingestion/observability/stages.ts";
import {
	BILLING_MOLLIE_CLASSIC_WEBHOOK_PATH,
	BILLING_MOLLIE_EVENTS_WEBHOOK_PATH,
	billingWebhookIngressBindingToken,
	billingWebhookPathKind,
} from "../billing/ingestion/urls.ts";
import { classifyMollieWebhookBody } from "../billing/runtime/local/providers/mollie/parse-webhook.ts";
import { MOLLIE_WEBHOOK_MAX_BODY_BYTES } from "../billing/runtime/local/providers/mollie/verify-webhook.ts";
import type { AthenaPostgresPool } from "../postgres/driver.ts";
import {
	getAthenaClientInternals,
	requireAthenaRootClientInternals,
} from "../runtime/client-internals.ts";
import { compileHttpWebhookIngress } from "../runtime/ingress/compiler/http.ts";
import {
	ATHENA_EVENT_INGRESS_FAILED,
	ATHENA_EVENT_INGRESS_PERSISTENCE_REQUIRED,
	ATHENA_EVENT_INGRESS_UNAVAILABLE,
	ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
	AthenaEventIngressError,
	eventIngressHttpStatus,
	isAthenaEventIngressError,
} from "../runtime/ingress/errors.ts";
import type { EventIngressRuntime } from "../runtime/ingress/runtime.ts";
import { isAthenaHttpBodyLimitError } from "../runtime/transport/http/incoming.ts";

export const DEFAULT_ATHENA_NEXT_BILLING_WEBHOOK_ENDPOINT =
	"/api/athena/billing/webhook";
export const DEFAULT_ATHENA_NEXT_BILLING_MOLLIE_CLASSIC_WEBHOOK_ENDPOINT =
	BILLING_MOLLIE_CLASSIC_WEBHOOK_PATH;
export const DEFAULT_ATHENA_NEXT_BILLING_MOLLIE_EVENTS_WEBHOOK_ENDPOINT =
	BILLING_MOLLIE_EVENTS_WEBHOOK_PATH;

export interface AthenaBillingIngressHandlers {
	POST: (request: Request) => Promise<Response>;
}

export interface CreateAthenaBillingIngressHandlersOptions {
	client: object;
	connectionId?: string;
}

function isAthenaPostgresPool(value: unknown): value is AthenaPostgresPool {
	return (
		value != null &&
		typeof value === "object" &&
		typeof (value as AthenaPostgresPool).query === "function" &&
		typeof (value as AthenaPostgresPool).connect === "function"
	);
}

async function resolveIngressConnectionId(input: {
	client: object;
	explicitConnectionId?: string;
	pathname: string;
}): Promise<string | undefined> {
	if (
		input.explicitConnectionId != null &&
		input.explicitConnectionId.length > 0
	) {
		return input.explicitConnectionId;
	}
	const token = billingWebhookIngressBindingToken(input.pathname);
	if (token == null) {
		return undefined;
	}
	const internals = getAthenaClientInternals(input.client);
	const pool = internals?.postgresRuntime
		? await internals.postgresRuntime.getPool()
		: undefined;
	if (!isAthenaPostgresPool(pool)) {
		throw new AthenaEventIngressError({
			code: ATHENA_EVENT_INGRESS_PERSISTENCE_REQUIRED,
			domain: "billing",
			message:
				"Billing webhook ingress bindings require PostgreSQL so the connection can be resolved.",
			retryable: true,
		});
	}
	const connectionId = await resolveBillingConnectionIdByWebhookIngressToken(
		createBillingSqlExecutor(pool),
		token,
	);
	if (connectionId == null) {
		throw new AthenaEventIngressError({
			code: ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
			domain: "billing",
			message: "Unknown billing webhook ingress binding.",
			provider: "mollie",
			retryable: false,
		});
	}
	return connectionId;
}

export function createAthenaBillingIngressHandlers(
	options: CreateAthenaBillingIngressHandlersOptions,
): AthenaBillingIngressHandlers {
	requireAthenaRootClientInternals(
		options.client,
		"createAthenaBillingIngressHandlers({ client })",
	);

	return {
		async POST(request: Request): Promise<Response> {
			const internals = getAthenaClientInternals(options.client);
			const observability =
				billingWebhookIngressObservabilityFromInternals(internals);
			const pathname = new URL(request.url).pathname;
			const pathKind = billingWebhookPathKind(pathname);
			const operation =
				pathKind === "classic"
					? "webhook.mollie.classic"
					: pathKind === "events"
						? "webhook.mollie.events"
						: "webhook";
			const kind = billingWebhookRegistrationKind(operation);
			const runtime = internals?.eventIngressRuntime as
				| EventIngressRuntime
				| undefined;
			if (!runtime) {
				const unavailable = new AthenaEventIngressError({
					code: ATHENA_EVENT_INGRESS_UNAVAILABLE,
					message: "EventIngressRuntime is not attached.",
					retryable: true,
				});
				await recordBillingWebhookIngressQuietly(
					observability,
					async (store) => {
						await store.recordRejected({
							code: unavailable.code,
							kind,
							stage: persistableWebhookRejectionStage(),
							...(options.connectionId
								? { connectionId: options.connectionId }
								: {}),
						});
					},
				);
				return new Response(
					JSON.stringify({
						code: unavailable.code,
						message: unavailable.message,
					}),
					{ headers: { "content-type": "application/json" }, status: 503 },
				);
			}
			let compiledBody: Uint8Array | undefined;
			try {
				const connectionId = await resolveIngressConnectionId({
					client: options.client,
					explicitConnectionId: options.connectionId,
					pathname,
				});
				const ingress = await compileHttpWebhookIngress({
					request,
					domain: "billing",
					operation,
					...(connectionId ? { connectionId } : {}),
					maxBodyBytes: MOLLIE_WEBHOOK_MAX_BODY_BYTES,
				});
				compiledBody = ingress.body;
				await recordBillingWebhookIngressQuietly(
					observability,
					async (store) => {
						const connectionId = ingress.connectionId ?? options.connectionId;
						await store.recordStage({
							kind,
							occurredAt: new Date(),
							stage: "received",
							ingressId: ingress.id,
							operation,
							provider: "mollie",
							...(connectionId ? { connectionId } : {}),
							...(ingress.correlationId
								? { correlationId: ingress.correlationId }
								: {}),
							...(ingress.traceId ? { traceId: ingress.traceId } : {}),
						});
						if (connectionId) {
							await store.recordDelivery({
								connectionId,
								event: "received",
								kind,
							});
						}
					},
				);
				const result = await runtime.ingest(ingress);
				const reconciliation = result.reconciliation ?? "skipped";
				return new Response(
					JSON.stringify({
						ok: true,
						duplicate: result.duplicate,
						reconciliation,
					}),
					{
						headers: { "content-type": "application/json" },
						status: result.duplicate ? 200 : 202,
					},
				);
			} catch (error) {
				if (isAthenaHttpBodyLimitError(error)) {
					return new Response(
						JSON.stringify({
							code: error.code,
							message: error.message,
						}),
						{
							headers: { "content-type": "application/json" },
							status: error.status,
						},
					);
				}
				const status = eventIngressHttpStatus(error);
				const code = isAthenaEventIngressError(error)
					? error.code
					: ATHENA_EVENT_INGRESS_FAILED;
				const contentType = request.headers.get("content-type") ?? undefined;
				const envelope =
					isAthenaEventIngressError(error) && error.diagnostics
						? {
								bodyBytes: error.diagnostics.bodyBytes,
								bodyKind: error.diagnostics.bodyKind,
								contentType: error.diagnostics.contentType ?? contentType,
							}
						: classifyMollieWebhookBody(
								compiledBody ?? new Uint8Array(),
								contentType,
							);
				console.error("[athena.billing] webhook ingress rejected", {
					code,
					contentType: envelope.contentType,
					bodyBytes: envelope.bodyBytes,
					bodyKind: envelope.bodyKind,
				});
				await recordBillingWebhookIngressQuietly(
					observability,
					async (store) => {
						await store.recordRejected({
							code: publicBillingRejectionCode(error),
							kind,
							metadata: {
								bodyBytes: envelope.bodyBytes,
								bodyKind: envelope.bodyKind,
								...(envelope.contentType
									? { contentType: envelope.contentType }
									: {}),
							},
							stage: persistableWebhookRejectionStage(),
							...(options.connectionId
								? { connectionId: options.connectionId }
								: {}),
						});
					},
				);
				return new Response(
					JSON.stringify({
						code,
						message: isAthenaEventIngressError(error)
							? error.code
							: "Webhook ingest failed.",
					}),
					{
						headers: { "content-type": "application/json" },
						status,
					},
				);
			}
		},
	};
}
